import React, { useRef, useState } from 'react';
import classnames from 'classnames';
import ListRenderGuide from '@gp/components/ListRenderGuide';
import TinyEditor from '@gp/components/TinyEditor';
import KeyUpdatesContents from '@gp/pages/Pipeline/ProjectDetails/ProjectUpdate/components/ProjectUpdateCard/KeyUpdatesContents';
import ReportOperationCard from '../../../components/ReportOperationCard';
import CommentTables, { ICommentTablesInstance } from './Tables';
import ReviewCommentTables from './ReviewTables';
import { handleLinkerTarget } from '@intelligence/utils/commonUtils';
import { DPS_REPORT_MODULE_KEY } from '../../constants';

import type { IBaseProps } from '@home/types/common';
import type { IDPSReportCalendarProjectSimpleVO } from '@gp/types/dpsReport/common';
import type { IDPSReportProjectCommentsUpdatesVO } from '@gp/types/dpsReport/v3';

// DPS Comment模块的value的类型
export interface ICommentValue {
  // 富文本的value
  comment: string;
  // 表格的value
  commentsUpdates: IDPSReportProjectCommentsUpdatesVO[];
}

interface ICommentProps extends IBaseProps {
  // 标题
  title?: string;
  onTitleChange?: (payload: string) => void;
  // 是否只读
  readonly?: boolean;
  // 是否loading
  isLoading?: boolean;
  // Project下拉筛选器options数据源
  projects?: IDPSReportCalendarProjectSimpleVO[] | null;
  // DPS Comment模块的value
  value: ICommentValue;
  // DPS Comment模块的value的change的回调
  onChange?: (payload: ICommentValue) => void;
}

export const CARD_TITLE = 'Important GR & Check-in Updates';

const Comment: React.FC<ICommentProps> = (props) => {
  const {
    style,
    className,
    title,
    onTitleChange,
    readonly = false,
    isLoading,
    projects,
    value,
    onChange,
  } = props;

  const { comment, commentsUpdates } = value || {};

  const tablesInstance = useRef<ICommentTablesInstance>(null);

  const [selection, setSelection] = useState<string[]>([]);

  const handleCommentChange = (payload: string) => {
    const valueTmp = {
      ...value,
      comment: payload,
    };
    onChange?.(valueTmp);
  };

  const handleCommentsUpdatesChange = (payload: IDPSReportProjectCommentsUpdatesVO[]) => {
    const valueTmp = {
      ...value,
      commentsUpdates: payload,
    };
    onChange?.(valueTmp);
  };

  const handleDelete = () => {
    tablesInstance.current?.delete();
  };

  return (
    <ReportOperationCard
      style={style}
      className={classnames('dbg-dps-report__comment-card', className)}
      moduleKey={DPS_REPORT_MODULE_KEY.COMMENT}
      title={title || CARD_TITLE}
      isSupportRenameTitle={!readonly}
      onTitleChange={onTitleChange}
      isLoading={isLoading}
      showDelete={!readonly && !!commentsUpdates?.length}
      disabledDelete={!selection?.length}
      onDelete={handleDelete}
    >
      {readonly && (
        <>
          <ListRenderGuide list={commentsUpdates}>
            <ReviewCommentTables value={commentsUpdates} />
          </ListRenderGuide>
          <div className="dbg-wang-editor-box" style={{ marginTop: 20 }}>
            {!!comment?.trim() && <KeyUpdatesContents data={handleLinkerTarget(comment)} />}
          </div>
        </>
      )}
      {readonly || (
        <>
          <CommentTables
            ref={tablesInstance}
            projects={projects}
            selection={selection}
            setSelection={setSelection}
            value={commentsUpdates}
            onChange={handleCommentsUpdatesChange}
          />
          <div className="dbg-wang-editor-box" style={{ marginTop: 20 }}>
            <TinyEditor
              className="gp-word-break-word"
              placeholder="Please input Project Updates"
              status="success"
              value={comment}
              getEditorContent={handleCommentChange}
            />
          </div>
        </>
      )}
    </ReportOperationCard>
  );
};

export default Comment;
